import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  FormsUtil,
  ListSelectComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  SelectComponent,
  SmartSearchComponent,
  SmartSearchModel,
  SmartSearchModelField,
  TableCellDirective,
  TableColumn,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { StorageDestinationsFacade } from 'mbs-ui-shared@facades/storage-destinations.facade';
import { StorageLimitsFacade } from 'mbs-ui-shared@facades/storage-limits.facade';
import { PermissionsEnum, StorageType } from 'mbs-ui-shared@models';
import { SmartSearchTemplateKeys } from 'mbs-ui-shared@models/SmartSearchTemplates';
import { StorageDestinationListUnit, StorageDestinationWithLimit } from 'mbs-ui-shared@models/StorageAccount';
import StorageLimit from 'mbs-ui-shared@models/StorageLimit';
import { SmartSearchTemplates } from 'mbs-ui-shared@services/smart-search-templates';
import { ADD_STORAGE_ACCOUNT_WIZARD_OPENER } from 'mbs-ui-shared@tokens/add-storage-account-wizard.token';
import { getDestinationKey, showStorageState, transformStorageStateValue } from 'mbs-ui-shared@utils/helpers/backup-destination';
import { StorageTypeIconPipe } from 'mbs-ui-shared@utils/pipes/storage-type-icon.pipe';
import { SmartSearchUtils } from 'mbs-ui-shared@utils/smartSearchUtils';
import { from, map, tap } from 'rxjs';
import { finalize } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'mbs-backup-destination-modal',
  templateUrl: './backup-destination-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    I18NextPipe,
    ListSelectComponent,
    ModalComponent,
    ModalFooterDirective,
    NgbTooltipModule,
    ReactiveFormsModule,
    SelectComponent,
    SmartSearchComponent,
    StorageTypeIconPipe,
    TableCellDirective,
    TextEllipsisComponent
  ]
})
export class BackupDestinationModalComponent implements OnInit {
  protected readonly ability = inject(AbilityService);
  private i18n = inject(I18NextPipe);
  private searchTemplatesService = inject(SmartSearchTemplates);
  private storageLimitsFacade = inject(StorageLimitsFacade);
  private destinationsFacade = inject(StorageDestinationsFacade);
  private openAddStorageAccountWizard = inject(ADD_STORAGE_ACCOUNT_WIZARD_OPENER, { optional: true });

  protected readonly PermissionsEnum = PermissionsEnum;
  protected readonly MbsPopupType = MbsPopupType;
  protected readonly StorageType = StorageType;
  protected readonly canAddDestination = !!this.openAddStorageAccountWizard;

  public readonly elementSelectors = { id: { createNewAlert: 'backup-destination-modal-create-new-alert' } };

  protected readonly transformStorageStateValue = transformStorageStateValue.bind(null, this.i18n);
  protected readonly getDestinationKey = getDestinationKey.bind(null, this.i18n);
  protected readonly showStorageState = showStorageState;

  protected readonly loading = signal<boolean>(false);
  protected readonly searchTemplates = [this.searchTemplatesService.storageAccountTypeItems];
  protected readonly headers: TableColumn[] = [
    {
      name: 'row',
      gridColSize: '1fr',
      class: '-stretch pl-0 -sm'
    }
  ];

  public allDestinations: StorageDestinationListUnit[];
  public filteredDestinations = signal<StorageDestinationListUnit[] | null>(null);

  public set destinations(destinations: StorageDestinationListUnit[]) {
    this.allDestinations = destinations;
    this.filteredDestinations.set(destinations);
  }
  public withLimits = false;
  public isLimitRequired = true;
  // Company context for the in-place wizard launch: hides its companies step and initializes the new account/destination
  public companyId: string;
  // Units to check right away — the tab's "Create new" flow reopens this modal with the fresh destination selected
  public preselectedIds: string[] = [];
  // The tab's "Create new" opens this modal and launches the wizard right away — one code path for both entries
  public startWithWizard = false;
  public selected = signal<StorageDestinationListUnit[]>([]);
  public storageLimits = signal<StorageLimit[] | null>(null);
  public storageLimitsControl = new FormControl(null);

  @ViewChild(ModalComponent, { static: true }) private baseModal;

  ngOnInit(): void {
    this.applyPreselection();

    if (this.startWithWizard && this.canAddDestination) this.openStorageWizard();

    if (this.withLimits) {
      this.isLimitRequired && this.storageLimitsControl.addValidators(Validators.required);

      this.loading.set(true);
      this.storageLimitsFacade
        .getLimits()
        .pipe(
          map((limits) => limits.filter((limit) => !!limit.name)),
          tap((limits) => this.storageLimits.set(limits)),
          finalize(() => this.loading.set(false))
        )
        .subscribe();
    }
  }

  public openStorageWizard(): void {
    const knownIds = new Set([...this.destinationsFacade.destinations(), ...(this.allDestinations ?? [])].map(({ id }) => id));

    this.loading.set(true);
    from(this.openAddStorageAccountWizard({ companyIds: this.companyId ? [this.companyId] : undefined }))
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((created) => created && this.appendCreatedDestinations(knownIds));
  }

  private appendCreatedDestinations(knownIds: Set<string>): void {
    this.destinationsFacade.load().subscribe((destinations) => {
      const created = destinations.filter(({ id }) => !knownIds.has(id));

      if (!created.length) return;

      // On top of the list, so the preselection is visible without scrolling
      const selected = [...created, ...this.selected()];
      this.allDestinations = [...created, ...(this.allDestinations ?? [])];
      this.filteredDestinations.set(this.allDestinations);
      // The grid resets and re-emits its selection on any data change, wiping ours — reapply it in the next macrotask
      setTimeout(() => this.selected.set(selected));
    });
  }

  private applyPreselection(): void {
    if (!this.preselectedIds.length) return;

    this.selected.set((this.allDestinations ?? []).filter(({ id }) => this.preselectedIds.includes(id)));
  }

  public updateFilters(event: SmartSearchModel): void {
    const filteredByStorageType = this.filterByStorageType(this.allDestinations, event);

    this.filteredDestinations.set(this.filterByQuery(filteredByStorageType, event));
  }

  public changeSelected(destinations: StorageDestinationListUnit[]): void {
    this.selected.set(destinations);

    if (!this.withLimits) {
      return;
    }

    const allStoragesAreAzureVM =
      !!destinations?.length && destinations.every((destination) => destination.storageType === StorageType.AzureVM);

    queueMicrotask(() => {
      allStoragesAreAzureVM ? this.storageLimitsControl.disable() : this.storageLimitsControl.enable();
    });
  }

  public onAddButtonClick() {
    const prepareResult = (): StorageDestinationWithLimit[] => this.selected();

    if (!this.withLimits) {
      return void this.baseModal.save(prepareResult());
    }

    if (!this.storageLimitsControl.valid && this.storageLimitsControl?.enabled) {
      return void FormsUtil.triggerValidation(this.storageLimitsControl);
    }

    return this.baseModal.save(
      prepareResult().map((destination) => ({
        ...destination,
        storageLimitId: this.storageLimitsControl?.value?.id,
        storageLimitName: this.storageLimitsControl?.value?.name
      }))
    );
  }

  private filterByStorageType(destinations: StorageDestinationListUnit[], search: SmartSearchModel): StorageDestinationListUnit[] {
    const storageTypeFilter = search[SmartSearchTemplateKeys.StorageType] as SmartSearchModelField[];

    if (!storageTypeFilter?.length) return destinations;

    return storageTypeFilter
      .map((entity) => entity.value)
      .reduce((acc, current) => {
        const filtered = destinations.filter((destination) => destination.storageType === StorageType[current]);

        return [...acc, ...filtered];
      }, []);
  }

  private filterByQuery(destinations: StorageDestinationListUnit[], search: SmartSearchModel): StorageDestinationListUnit[] {
    if (!search.words) return destinations;

    const query = SmartSearchUtils.getJoinedValue(SmartSearchUtils.getValues(search), 'words').toLowerCase();

    return destinations.filter((destination) => {
      return destination?.destinationName?.toLowerCase().includes(query) || destination?.bucketName?.toLowerCase().includes(query);
    });
  }
}
